import type { LexicalEditor } from 'lexical';
import { $createParagraphNode, $getRoot, $getSelection, $isElementNode, $isRangeSelection } from 'lexical';
import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { useAudioTranscription } from '~/shared/audio-transcription';
import { ApplicationContext } from '~/shared/context/context';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { TranscribeButton } from '../components/TranscribeButton';

type RichEditorTranscribeButtonProps = {
    readonly disabled?: boolean;
    readonly editor: LexicalEditor;
};

export const insertTranscriptionChunk = (text: string): void => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
        selection.insertText(text);

        return;
    }

    const lastChild = $getRoot().getLastChild();

    if ($isElementNode(lastChild) && lastChild.isInline() === false) {
        lastChild.selectEnd().insertText(text);

        return;
    }

    const paragraph = $createParagraphNode();
    $getRoot().append(paragraph);
    paragraph.selectEnd().insertText(text);
};

export const RichEditorTranscribeButton: FunctionComponent<RichEditorTranscribeButtonProps> = ({
    disabled,
    editor,
}) => {
    const { realtimeEventListenerBuilder, translate } = useContext(ApplicationContext);

    const handleTranscriptionChunk = useCallback(
        (text: string) => {
            editor.update(() => {
                insertTranscriptionChunk(text);
            });
        },
        [editor],
    );

    const { isRecording, isTranscribing, startRecording, stopRecording } = useAudioTranscription({
        onChunk: handleTranscriptionChunk,
        onComplete: noop,
        onError: noop,
        realtimeEventListenerBuilder,
    });

    const handleButtonClick = (): void => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const tooltipTitle = isRecording
        ? translate('common.stop-recording')
        : translate('common.start-audio-transcription');

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                <span style={{ display: 'inline-flex' }}>
                    <TranscribeButton
                        disabled={disabled}
                        isRecording={isRecording}
                        isTranscribing={isTranscribing}
                        onClick={handleButtonClick}
                    />
                </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content sideOffset={5} style={{ zIndex: 1300 }}>
                    {tooltipTitle}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
